import { inject, Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subject } from 'rxjs';

import { AuthService } from '../../clients/core/auth/service/auth.service';
import { OrganizationsService } from '../../clients/core/organizations/organizations.service';
import { UserService } from '../../clients/core/user/user.service';
import { PlatformConfigService } from '../config/config.service';
import {
  SpanEvent as BaseSpanEvent,
  SpanKind,
  SpanStatus,
  TraceExporterOptions,
} from './monitoring.type';

// Define our own SpanEvent type to avoid type conflicts
type SpanEvent = BaseSpanEvent & {
  traceId: string;
  spanId: string;
  parentSpanId?: string;
  name: string;
  kind: SpanKind;
  startTimeUnixNano: number;
  endTimeUnixNano: number;
  attributes: Record<string, string | number | boolean>;
  status: SpanStatus;
  events?: {
    name: string;
    timeUnixNano: number;
    attributes: Record<string, string | number | boolean>;
  }[];
};

@Injectable({
  providedIn: 'root',
})
export class MonitoringService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly activePageSpans = new Map<string, string>();
  private readonly activeOperationSpans = new Map<string, string>();
  private readonly pendingSpans: SpanEvent[] = [];

  private userId: string | null = null;
  private organizationId: string | null = null;
  private isEnabled = false;
  private isInitialized = false;
  private sendToTempo = false;
  private debugEnabled = false;
  private appName: string;
  private options: TraceExporterOptions;
  private flushInterval: ReturnType<typeof setInterval> | null = null;

  private configService = inject(PlatformConfigService);
  private userService = inject(UserService);
  private organizationsService = inject(OrganizationsService);
  private authService = inject(AuthService);
  private router = inject(Router);

  constructor() {
    // Use a default endpoint for Tempo if not provided in config
    const configEndpoint = this.configService.get('TEMPO_ENDPOINT');
    const endpoint =
      typeof configEndpoint === 'string'
        ? configEndpoint
        : 'https://otlp.api.ca.connect.dev-evcnt.com/v1/traces';
    const appConfig = this.configService.get('app');

    this.options = {
      endpoint,
      organizationHeader: 'X-Scope-OrgID',
      organizationId: 'connect.evidentscientific', // Default Tempo organization ID (not user's organization)
      serviceName:
        appConfig && typeof appConfig === 'object' && 'name' in appConfig
          ? String(appConfig.name)
          : 'evident-connect-app',
      batchSize: 10,
      flushIntervalMs: 5000,
    };

    this.appName = this.options.serviceName;
    this.debugEnabled =
      this.configService.getEnv('DEBUG_MONITORING') === true ||
      this.configService.getEnv('DEBUG_MONITORING') === 'true';

    // If debug is enabled, we don't send to Tempo (local development mode)
    // If debug is disabled, we send to Tempo (production mode)
    this.sendToTempo = !this.debugEnabled;

    // Note: initialize() is called lazily on first use to avoid circular dependencies
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.flushInterval) clearInterval(this.flushInterval);
    this.flush();
  }

  initialize(): void {
    if (this.isInitialized) return;
    this.isInitialized = true;

    const monitoringEnabled =
      this.configService.get('MONITORING_ENABLED') && !location.origin.includes('localhost');
    this.isEnabled =
      monitoringEnabled === true ||
      (typeof monitoringEnabled === 'string' && monitoringEnabled === 'true');

    if (!this.isEnabled) {
      return;
    }

    if (this.debugEnabled) {
      // eslint-disable-next-line no-console
      console.debug(`[Monitoring] Initializing monitoring for ${this.appName}`);
    }

    const checkUserProfile = () => {
      const profile = this.userService.profile();
      if (profile && profile.id) {
        this.userId = profile.id;
        if (this.debugEnabled) {
          // eslint-disable-next-line no-console
          console.debug(`[Monitoring] User identified: ${this.userId}`);
        }
      }

      // Try to get organization ID from multiple sources
      this.updateOrganizationId();
    };

    checkUserProfile();
    const profileCheckInterval = setInterval(checkUserProfile, 5000);

    this.destroy$.subscribe(() => clearInterval(profileCheckInterval));

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.trackPageView(event.urlAfterRedirects);
      });

    const flushIntervalMs = this.options?.flushIntervalMs || 5000;
    this.flushInterval = setInterval(() => {
      this.flush();
    }, flushIntervalMs);
  }

  private ensureInitialized(): void {
    if (!this.isInitialized) {
      this.initialize();
    }
  }

  trackPageView(url: string): void {
    if (!this.isEnabled) return;

    this.endActivePageSpan();

    const attributes = {
      'page.url': url,
      'app.name': this.appName,
      'user.id': this.userId || 'anonymous',
      'organization.id': this.organizationId || 'unknown',
    };

    const spanId = this.startSpan('page_view', attributes);
    this.activePageSpans.set(url, spanId);
  }

  endActivePageSpan(): void {
    if (!this.isEnabled || this.activePageSpans.size === 0) return;

    for (const [url, spanId] of this.activePageSpans.entries()) {
      this.endSpan(spanId, SpanStatus.OK);
      this.activePageSpans.delete(url);
    }
  }

  trackOperation(
    operation: string,
    resourceId: string,
    metadata: Record<string, string | number | boolean> = {},
  ): string {
    this.ensureInitialized();
    if (!this.isEnabled) return '';

    // Always get the latest organization ID
    this.updateOrganizationId();
    const currentOrgId = this.organizationId || 'unknown';

    const attributes = {
      'operation.type': operation,
      'resource.id': resourceId,
      'app.name': this.appName,
      'user.id': this.userId || 'anonymous',
      'organization.id': currentOrgId,
      ...metadata,
    };

    const spanId = this.startSpan(`operation.${operation}`, attributes);
    this.activeOperationSpans.set(`${operation}_${resourceId}`, spanId);

    return spanId;
  }

  completeOperation(
    operation: string,
    resourceId: string,
    success: boolean,
    metadata: Record<string, string | number | boolean> = {},
  ): void {
    this.ensureInitialized();
    if (!this.isEnabled) return;

    const key = `${operation}_${resourceId}`;
    const spanId = this.activeOperationSpans.get(key);

    if (!spanId) {
      if (this.debugEnabled) {
        console.warn(`No active span found for operation: ${operation} on resource: ${resourceId}`);
      }

      return;
    }

    if (Object.keys(metadata).length > 0) {
      this.addSpanAttributes(spanId, metadata);
    }

    if (success) {
      this.endSpan(spanId, SpanStatus.OK);
    } else {
      this.endSpan(spanId, SpanStatus.ERROR);
    }

    this.activeOperationSpans.delete(key);
  }

  trackApiCall<T>(
    endpoint: string,
    method: string,
    fn: () => Promise<T>,
    metadata: Record<string, string | number | boolean> = {},
  ): Promise<T> {
    if (!this.isEnabled) return fn();

    // Always get the latest organization ID
    this.updateOrganizationId();
    const currentOrgId = this.organizationId || 'unknown';

    const attributes = {
      'http.url': endpoint,
      'http.method': method,
      'app.name': this.appName,
      'user.id': this.userId || 'anonymous',
      'organization.id': currentOrgId,
      ...metadata,
    };

    return this.recordSpan(`api.${method.toLowerCase()}`, attributes, fn);
  }

  trackUserAction(action: string, details: Record<string, string | number | boolean> = {}): void {
    if (!this.isEnabled) return;

    // Always get the latest organization ID
    this.updateOrganizationId();
    const currentOrgId = this.organizationId || 'unknown';

    const attributes = {
      'action.type': action,
      'app.name': this.appName,
      'user.id': this.userId || 'anonymous',
      'organization.id': currentOrgId,
      ...details,
    };

    const spanId = this.startSpan(`user_action.${action}`, attributes);
    this.endSpan(spanId, SpanStatus.OK);
  }

  trackError(
    error: Error,
    context: string,
    metadata: Record<string, string | number | boolean> = {},
  ): void {
    this.ensureInitialized();
    if (!this.isEnabled) return;

    // Always get the latest organization ID
    this.updateOrganizationId();
    const currentOrgId = this.organizationId || 'unknown';

    const attributes: Record<string, string | number | boolean> = {
      'error.message': error.message,
      'error.stack': error.stack || '',
      'error.context': context,
      'app.name': this.appName,
      'user.id': this.userId || 'anonymous',
      'organization.id': currentOrgId,
      ...metadata,
    };

    const spanId = this.startSpan('error', attributes);
    this.endSpan(spanId, SpanStatus.ERROR);
  }

  startSpan(
    name: string,
    attributes: Record<string, string | number | boolean> = {},
    parentSpanId?: string,
  ): string {
    if (!this.isEnabled) return '';

    const traceId = this.generateTraceId();
    const spanId = this.generateSpanId();

    const span: SpanEvent = {
      traceId,
      spanId,
      parentSpanId,
      name,
      kind: SpanKind.INTERNAL,
      startTimeUnixNano: Date.now() * 1000000,
      endTimeUnixNano: 0,
      attributes,
      status: SpanStatus.UNSET,
      timeUnixNano: Date.now() * 1000000, // Add required timeUnixNano property
    };

    this.pendingSpans.push(span);
    // Log span start if debug is enabled
    if (this.debugEnabled) {
      // eslint-disable-next-line no-console
      console.debug(`[Monitoring] Started span: ${name}`, { spanId, attributes });
    }

    // If we have enough spans, flush them
    const batchSize = this.options?.batchSize || 10;
    if (this.pendingSpans.length >= batchSize) {
      this.flush();
    }

    return spanId;
  }

  endSpan(spanId: string, status: SpanStatus = SpanStatus.OK): void {
    if (!this.isEnabled) return;

    const span = this.pendingSpans.find((s) => s.spanId === spanId);
    if (!span) {
      console.warn(`[Monitoring] No span found with ID: ${spanId}`);

      return;
    }

    span.endTimeUnixNano = Date.now() * 1000000;
    span.status = status;

    // Log span end if debug is enabled
    if (this.debugEnabled) {
      // eslint-disable-next-line no-console
      console.debug(`[Monitoring] Ended span: ${span.name}`, {
        spanId,
        duration: (span.endTimeUnixNano - span.startTimeUnixNano) / 1000000,
        status,
      });
    }
  }

  addSpanEvent(
    spanId: string,
    name: string,
    attributes: Record<string, string | number | boolean> = {},
  ): void {
    if (!this.isEnabled) return;

    const span = this.pendingSpans.find((s) => s.spanId === spanId);
    if (!span) {
      console.warn(`[Monitoring] No span found with ID: ${spanId}`);

      return;
    }

    if (!span.events) {
      span.events = [];
    }

    span.events.push({
      name,
      timeUnixNano: Date.now() * 1000000,
      attributes,
    });
  }

  addSpanAttributes(spanId: string, attributes: Record<string, string | number | boolean>): void {
    if (!this.isEnabled) return;

    const span = this.pendingSpans.find((s) => s.spanId === spanId);
    if (!span) {
      console.warn(`[Monitoring] No span found with ID: ${spanId}`);

      return;
    }

    span.attributes = {
      ...span.attributes,
      ...attributes,
    };
  }

  async recordSpan<T>(
    name: string,
    attributes: Record<string, string | number | boolean>,
    fn: () => Promise<T>,
  ): Promise<T> {
    if (!this.isEnabled) return fn();

    const spanId = this.startSpan(name, attributes);

    try {
      const result = await fn();
      this.endSpan(spanId, SpanStatus.OK);

      return result;
    } catch (error) {
      this.endSpan(spanId, SpanStatus.ERROR);

      throw error;
    }
  }

  async flush(): Promise<void> {
    if (this.pendingSpans.length === 0) {
      return;
    }

    const spans = [...this.pendingSpans];
    this.pendingSpans.length = 0;

    // Log span flush if debug is enabled
    if (this.debugEnabled) {
      // eslint-disable-next-line no-console
      console.debug(`[Monitoring] Flushed ${spans.length} spans`);
    }

    // Store in localStorage for debugging
    this.storeTraceInLocalStorage({ spans });

    // If debug is disabled and we have a valid endpoint, send to Tempo
    if (this.sendToTempo && this.options.endpoint) {
      try {
        const payload = {
          resourceSpans: [
            {
              resource: {
                attributes: [
                  {
                    key: 'service.name',
                    value: {
                      stringValue: this.options.serviceName,
                    },
                  },
                ],
              },
              scopeSpans: [
                {
                  scope: {
                    name: 'monitoring',
                  },
                  spans: spans.map((span) => ({
                    traceId: span.traceId,
                    spanId: span.spanId,
                    parentSpanId: span.parentSpanId,
                    name: span.name,
                    kind: span.kind,
                    startTimeUnixNano: span.startTimeUnixNano,
                    endTimeUnixNano: span.endTimeUnixNano,
                    attributes: Object.entries(span.attributes || {}).map(([key, value]) => ({
                      key,
                      value: {
                        stringValue: String(value),
                      },
                    })),
                    status: {
                      code: span.status,
                    },
                  })),
                },
              ],
            },
          ],
        };

        // Send to Tempo endpoint
        const headers = new Headers({
          'Content-Type': 'application/json',
        });

        if (this.options.organizationHeader && this.options.organizationId) {
          headers.append(this.options.organizationHeader, this.options.organizationId);
        }

        await fetch(this.options.endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
        });

        // Log successful Tempo send if debug is enabled
        if (this.debugEnabled) {
          // eslint-disable-next-line no-console
          console.debug(`[Monitoring] Sent ${spans.length} spans to Tempo`);
        }
      } catch (error) {
        if (this.debugEnabled) {
          console.error('[Monitoring] Error sending spans to Tempo:', error);
        }
      }
    }
  }

  private storeTraceInLocalStorage(trace: { spans: SpanEvent[] }): void {
    try {
      const traces = JSON.parse(localStorage.getItem('evcnt_monitoring_traces') || '[]');
      traces.push({
        timestamp: new Date().toISOString(),
        ...trace,
      });

      // Keep only the last 10 traces
      if (traces.length > 10) {
        traces.shift();
      }

      localStorage.setItem('evcnt_monitoring_traces', JSON.stringify(traces));
    } catch (error) {
      if (this.debugEnabled) {
        console.error('[Monitoring] Error storing trace in localStorage:', error);
      }
    }
  }

  private generateId(bytes = 16): string {
    const hexChars = '0123456789abcdef';
    let id = '';
    // Each byte is 2 hex characters
    for (let i = 0; i < bytes * 2; i++) {
      id += hexChars[Math.floor(Math.random() * 16)];
    }

    return id;
  }

  private generateTraceId(): string {
    return this.generateId(16); // 16 bytes = 32 hex chars
  }

  private generateSpanId(): string {
    return this.generateId(8); // 8 bytes = 16 hex chars
  }

  public isDebugEnabled(): boolean {
    return this.debugEnabled;
  }

  private updateOrganizationId(): void {
    // Try to get organization ID from the auth service's computed property
    try {
      const orgId = this.authService.organizationId();
      if (orgId) {
        this.organizationId = orgId;
        if (this.debugEnabled) {
          // eslint-disable-next-line no-console
          console.debug(
            `[Monitoring] Organization identified from auth service: ${this.organizationId}`,
          );
        }

        return;
      }
    } catch (e) {
      // Log error but continue
      if (this.debugEnabled) {
        // eslint-disable-next-line no-console
        console.debug(`[Monitoring] Error getting organization ID from auth service: ${e}`);
      }
    }

    // Fallback to organizations service
    try {
      const orgIdSignal = this.organizationsService.currentOrgId;
      const orgId = orgIdSignal();
      if (orgId) {
        this.organizationId = orgId;
        if (this.debugEnabled) {
          // eslint-disable-next-line no-console
          console.debug(
            `[Monitoring] Organization identified from organizations service: ${this.organizationId}`,
          );
        }

        return;
      }
    } catch (e) {
      // Log error but continue
      if (this.debugEnabled) {
        // eslint-disable-next-line no-console
        console.debug(
          `[Monitoring] Error getting organization ID from organizations service: ${e}`,
        );
      }
    }
  }
}
